/**
 * Hook for streaming chat via SSE.
 *
 * Backend is stateless (post-TRA-760) — no session_id, no DB persistence.
 * Production traffic flows through the gateway directly and lands in R2
 * as traces; this hook is only for the in-browser playground.
 *
 * SSE event types from `/api/chat`: text_delta, thinking_delta,
 * tool_call_start, tool_call_delta, done, error. The envelope is parsed by
 * `lib/sse`, shared with the review page's ask-about-this-run chat.
 */

import { useCallback, useRef, useState } from 'react'

import { apiHeaders, apiUrl, getImpersonateTeamId } from '@/api/client'
import { readErrorDetail, readSseStream } from '@/lib/sse'

export interface Message {
  role: 'user' | 'assistant'
  content: string
}

export interface UseStreamingChatOptions {
  modelId?: string
  systemPrompt?: string
}

export interface UseStreamingChatReturn {
  messages: Message[]
  sendMessage: (content: string) => Promise<void>
  isStreaming: boolean
  error: string | null
  stop: () => void
  clearMessages: () => void
}

export function useStreamingChat({
  modelId,
  systemPrompt,
}: UseStreamingChatOptions = {}): UseStreamingChatReturn {
  const [messages, setMessages] = useState<Message[]>([])
  const [isStreaming, setIsStreaming] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const abortControllerRef = useRef<AbortController | null>(null)

  const stop = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
      abortControllerRef.current = null
    }
    setIsStreaming(false)
  }, [])

  const sendMessage = useCallback(
    async (content: string) => {
      if (!modelId) {
        setError('No model selected')
        return
      }

      // Chat is a write/spend operation. Impersonation is strictly read-only
      // (the backend 403s the POST), so block it here for a clear message
      // rather than a raw network error.
      if (getImpersonateTeamId()) {
        setError('Chat is disabled while impersonating a team (read-only).')
        return
      }

      const userMessage: Message = { role: 'user', content }
      setMessages((prev) => [...prev, userMessage])
      setIsStreaming(true)
      setError(null)

      // Add an empty assistant placeholder so the streamed deltas have
      // somewhere to land.
      setMessages((prev) => [...prev, { role: 'assistant', content: '' }])

      const abortController = new AbortController()
      abortControllerRef.current = abortController

      try {
        const response = await fetch(apiUrl('/api/chat'), {
          method: 'POST',
          headers: apiHeaders({ 'Content-Type': 'application/json' }),
          signal: abortController.signal,
          body: JSON.stringify({
            model_id: modelId,
            messages: [...messages, userMessage].map((m) => ({
              role: m.role,
              content: m.content,
            })),
            system_prompt: systemPrompt ?? undefined,
          }),
        })

        if (!response.ok) {
          throw new Error(await readErrorDetail(response))
        }

        if (!response.body) {
          throw new Error('Response body is not readable')
        }

        await readSseStream(
          response.body,
          (event) => {
            switch (event.type) {
              case 'text_delta': {
                setMessages((prev) => {
                  const updated = [...prev]
                  const lastIdx = updated.length - 1
                  const lastMsg = updated[lastIdx]
                  if (lastMsg && lastMsg.role === 'assistant') {
                    updated[lastIdx] = {
                      role: 'assistant',
                      content: lastMsg.content + event.content,
                    }
                  }
                  return updated
                })
                break
              }
              case 'thinking_delta':
              case 'tool_call_start':
              case 'tool_call_delta':
              case 'done': {
                // Not surfaced in the playground UI yet.
                break
              }
              case 'error': {
                setError(event.content)
                setMessages((prev) => {
                  const last = prev[prev.length - 1]
                  if (last && last.role === 'assistant' && last.content === '') {
                    return prev.slice(0, -1)
                  }
                  return prev
                })
                break
              }
            }
          },
          (raw) => {
            console.error('[useStreamingChat] Failed to parse SSE event:', raw)
          },
        )
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') {
          return
        }
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to send message'
        setError(errorMessage)
        setMessages((prev) => {
          const last = prev[prev.length - 1]
          if (last && last.role === 'assistant' && last.content === '') {
            return prev.slice(0, -1)
          }
          return prev
        })
      } finally {
        setIsStreaming(false)
        abortControllerRef.current = null
      }
    },
    [modelId, systemPrompt, messages],
  )

  const clearMessages = useCallback(() => {
    setMessages([])
    setError(null)
  }, [])

  return {
    messages,
    sendMessage,
    isStreaming,
    error,
    stop,
    clearMessages,
  }
}
